export type DefaultNodeType = { id: string, type: string, weight?: number, selected?: number }
import {_, deepdash, observable} from '@api'
import {DEBUG_ASSORTMENTS} from '@modules'
import {IDisposer} from 'mobx-utils'
import {AttributeDefinitions, BorderNode, DockLocation, DropInfo, IDraggable, IJsonBorderNode, IJsonRowNode, IJsonTabNode, IJsonTabSetNode, ILayoutMetrics, Model, Orientation, Rect, RowNode, SplitterNode, TabNode, TabSetNode} from '../'
import {globalStores} from '@stores'
import {guid} from '@utils'
import {action, computed} from 'mobx'

export abstract class FlexLayoutNode<T extends { id: string, type: string | 'tab' | 'tabset' | 'splitter', weight?: number, selected?: number } = DefaultNodeType, U extends IJsonBorderNode | IJsonRowNode | IJsonTabNode | IJsonTabSetNode = {}> {
  @computed get nonSidebarChildren() {
    return this.children.filter(f => !f.isLayoutSidebar)
  }

  get flexLayout() { return globalStores.flexLayout}

  @action addChildren = (children: Array<FlexLayoutNode>) => {
    const {_children} = this
    children.forEach(t => t.parent = this)
    _children.push(...children)
  }

  @computed get hidden() {
    if (this.parent && this.parent !== this.model.root) {
      return this.parent.hidden
    }
    else {
      const {id} = this
      if (id.endsWith('Sidebar')) {
        const borderId = `border_${id.replaceAll('Sidebar', '')}`,
          b = this.model.nodeById<BorderNode>(borderId)

        return b?.isCollapsed === true
      }
      return false
    }
  }

  @action removeFromParent(removeIfEmpty = true) {
    const {parent, model} = this
    if (parent) {
      const {indexInParent} = this
      parent._children = parent._children.filter(n => n.id != this.id)
      parent._setSelected(indexInParent - 1)

      if (removeIfEmpty) {
        if (_.isEmpty(parent._children)) {
          parent.removeFromParent()
        } else {
          this.model.onChange()
        }
      }
    }

    !removeIfEmpty && this.model.onChange()
  }

  @observable lastSplitWeights: { l: number, r: number } = null

  @computed get leftOrTop() {
    if (!this.parent) return null

    const {indexInParent: i, parent: {children}} = this
    return i === 0 ? null : children[i - 1]
  }

  @computed get rightOrBottom() {
    if (!this.parent) return null
    const {indexInParent: i, parent: {children}} = this
    return i + 1 === children.length ? null : children[i + 1]
  }

  @computed get isLayoutSidebar () {
    const {id, flexLayout: {knownTabs}, type} = this

    if (id?.endsWith('Sidebar') || this.parent?.id?.endsWith('Sidebar') || this._children.some(t => knownTabs[t.id] != null)) return true

    return false
    //return deepdash.findDeep(this, (v, k) => k === '_children' && v.some(c => knownTabs[c.id] != null), {leavesOnly: false}) != null
  }

  @computed get indexInParent() {
    const {parent} = this
    if (!parent) return -1

    if (this instanceof SplitterNode) return parent.drawChildren.indexOf(this)

    return parent._children.findIndex(v => v.id === this.id)
  }

  @computed get isPreviewLayout() {
    return this._model.isPreview
  }

  get children() { return this._children}

  @computed get json() {
    const r = this._model.jsonForId(this.id)
    if (!r) {
      const item = this.model.nodeById(this.id)
      if (item) return item.asJson

      // debugger
    }
    return r
  }

  @computed get model() {
    return this._model
  }

  @computed get layout() {
    const {isPreviewLayout, flexLayout} = this
    return (isPreviewLayout ? flexLayout.previewLayout : flexLayout.layout).store
  }

  /** @hidden @internal */
  @observable.ref _model: Model
  /** @hidden @internal */
  @observable _attributes: Partial<T>

  @computed get attributes() { return this._attributes }

  /** @hidden @internal */
  @observable.ref public parent?: FlexLayoutNode
  /** @hidden @internal */
  @observable public _children: FlexLayoutNode[]
  /** @hidden @internal */
  @observable protected _fixed: boolean
  /** @hidden @internal */
  @observable protected _rect: Rect = Rect.empty()

  get rect() { return this._rect}
  /** @hidden @internal */
  protected _listeners: Record<string, (params: any) => void>
  /** @hidden @internal */
  @observable _dirty: boolean = false
  /** @hidden @internal */
  protected _tempSize: number = 0

  /** @hidden @internal */
  protected constructor(model: Model) {
    this._model = model
    this._attributes = {}
    this._children = []
    this._fixed = false
    this._rect = Rect.empty()
    this._listeners = {}
  }

  @computed get id() {
    let id = this._attributes.id
    if (id !== undefined) {
      return id as string
    }

    id = this._model._nextUniqueId()
    this._setId(id)

    return id as string
  }

  @computed get type() {
    return this._attributes.type as string
  }
  
  @computed get isHorizontal() {
    return this.orientation.name === 'horz'
  }

  @computed get isVertical() {
    return this.orientation.name === 'vert'
  }

  get orientation(): Orientation {
    if (this.parent === undefined) {
      return this._model.isRootOrientationVertical ? Orientation.VERT : Orientation.HORZ
    } else {
      return Orientation.flip(this.parent.orientation)
    }
  }

  // event can be: resize, visibility, maximize (on tabset), close
  setEventListener(event: string, callback: (params: any) => void) {
    this._listeners[event] = callback
  }

  removeEventListener(event: string) {
    delete this._listeners[event]
  }

  /** @hidden @internal */
  _setId(id: string) {
    this._attributes.id = id
  }

  /** @hidden @internal */
  @action _fireEvent = (event: string, params: any) => {
    // console.log(this._type, " fireEvent " + event + " " + JSON.stringify(params));
    if (this._listeners[event] !== undefined) {
      this._listeners[event](params)
    }
  }

  /** @hidden @internal */
  _getAttr = (name: string) => {
    let val = this._attributes[name]

    if (val === undefined) {
      const modelName = this._getAttributeDefinitions().getModelName(name)
      if (modelName !== undefined) {
        val = this._model._getAttribute(modelName)
      }
    }

    // console.log(name + "=" + val);
    return val
  }

  /** @hidden @internal */
  _forEachNode = (fn: (node: FlexLayoutNode, level: number) => void, level: number) => {
    fn(this, level)
    level++
    this._children.forEach((node) => {
      node._forEachNode(fn, level)
    })
  }

  /** @hidden @internal */
  @computed get drawChildren(): FlexLayoutNode[] | undefined {
    return this._children
  }

  /** @hidden @internal */
  @action _setParent = (parent: FlexLayoutNode) => {
    this.parent = parent
  }

  /** @hidden @internal */
  @action _setRect = (rect: Rect) => {
    if (!this._rect?.equals(rect)) {
      this._rect = rect
    }
  }

  /** @hidden @internal */
  _setWeight = (weight: number) => {
    // if (weight === 0) debugger

    this._attributes.weight = weight
  }

  /** @hidden @internal */
  _setSelected = (index: number) => {
    this._attributes.selected = index
  }

  /** @hidden @internal */
  _isFixed() {
    return this._fixed
  }

  /** @hidden @internal */
  _layout = (rect: Rect, metrics: ILayoutMetrics) => {
    if (!this._rect?.equals(rect)) {
      this._rect = rect
    }
  }

  /** @hidden @internal */
  _findDropTargetNode = (dragNode: FlexLayoutNode & IDraggable, x: number, y: number): DropInfo | undefined => {
    let rtn: DropInfo | undefined
    if (this.rect.contains(x, y)) {
      rtn = this.canDrop(dragNode, x, y)
      if (rtn === undefined) {
        if (this._children.length !== 0) {
          for (const child of this._children) {
            rtn = child._findDropTargetNode(dragNode, x, y)
            if (rtn !== undefined) {
              break
            }
          }
        }
      }
    }

    return rtn
  }

  /** @hidden @internal */
  canDrop(dragNode: FlexLayoutNode & IDraggable, x: number, y: number): DropInfo | undefined {
    return undefined
  }

  /** @hidden @internal */
  _canDockInto(dragNode: FlexLayoutNode & IDraggable, dropInfo: DropInfo | undefined): boolean {
    if (dropInfo != null) {
      if (dropInfo.location === DockLocation.CENTER && dropInfo.node.isEnableDrop === false) {
        return false
      }

      // prevent named tabset docking into another tabset, since this would lose the header
      if (dropInfo.location === DockLocation.CENTER && dragNode.type === 'tabset' /*&& dragNode.name !== undefined*/) {
        return false
      }

      if (dropInfo.location !== DockLocation.CENTER && dropInfo.node.isEnableDivide === false) {
        return false
      }

      // finally check model callback to check if drop allowed
      if (this._model._getOnAllowDrop()) {
        return (this._model._getOnAllowDrop() as (dragNode: FlexLayoutNode, dropInfo: DropInfo) => boolean)(dragNode, dropInfo)
      }
    }
    return true
  }

  /** @hidden @internal */
  @action _removeChild = (childNode: FlexLayoutNode) => {
    const pos = this._children.findIndex(v => v.id === childNode.id)

    if (pos !== -1) {
      this._children.splice(pos, 1)
    }
    else {
      console.warn(`Unable to remove flex child - not found in parent`, {parent: this, childNode})
    }

    this._dirty = true
    return pos
  }


  /** @hidden @internal */
  @action addChild = (childNode: FlexLayoutNode, pos?: number) => {
    this.flexLayout._updateIsFromLayoutModel = true
    if (pos != null) {
      // DEBUG && console.debug(`Inserting tab ${childNode.id} at position ${pos} in tabset ${this.id} `)
      this._children.splice(pos, 0, childNode)
    } else {
      this._children.push(childNode)
      pos = this._children.length - 1
    }
    childNode.parent = this
    this._dirty = true
    return pos
  }

  /** @hidden @internal */
  @action _removeAll = () => {
    this._children = []
    this._dirty = true
  }

  /** @hidden @internal */
  _styleWithPosition = (style?: Record<string, any>) => {
    if (style == null) {
      style = {}
    }
    return this.rect.styleWithPosition(style)
  }

  /** @hidden @internal */
  _getTempSize() {
    return this._tempSize
  }

  /** @hidden @internal */
  _setTempSize(value: number) {
    this._tempSize = value
  }

  /** @hidden @internal */
  get isEnableDivide() {
    return true
  }

  /** @hidden @internal */
  _toAttributeString() {
    return JSON.stringify(this._attributes, undefined, '\t')
  }

  // implemented by subclasses
  /** @hidden @internal */
  abstract _updateAttrs(json: any): void;

  /** @hidden @internal */
  abstract _getAttributeDefinitions(): AttributeDefinitions;

  /** @hidden @internal */
  abstract get asJson(): any;

  @action delete = () => {
    // Abstract
  }

  @computed get isInModel() {
    return this.model._idMap[this.id] != null
  }

  registerEffects = () : IDisposer[] => {
    return []
  }

  select = () => {

  }
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/model/FlexLayoutNode.ts